/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimationOptions, ɵStyleData} from '@angular/animations';

import {AnimationDriver} from '../render/animation_driver';
import {getOrSetAsInMap} from '../render/shared';
import {iteratorToArray, mergeAnimationOptions} from '../util';

import {TransitionAst} from './animation_ast';
import {buildAnimationTimelines} from './animation_timeline_builder';
import {TransitionMatcherFn} from './animation_transition_expr';
import {AnimationTransitionInstruction, createTransitionInstruction} from './animation_transition_instruction';
import {ElementInstructionMap} from './element_instruction_map';
export class AnimationTransitionFactory {
/**
 * @param {?} _triggerName
 * @param {?} ast
 * @param {?} _stateStyles
 */
constructor(
private _triggerName: string,
public ast: TransitionAst,
private _stateStyles: {[stateName: string]: ɵStyleData}) {}
/**
 * @param {?} currentState
 * @param {?} nextState
 * @return {?}
 */
match(currentState: any, nextState: any): boolean {
    return oneOrMoreTransitionsMatch(this.ast.matchers, currentState, nextState);
  }
/**
 * @param {?} driver
 * @param {?} element
 * @param {?} currentState
 * @param {?} nextState
 * @param {?=} options
 * @param {?=} subInstructions
 * @return {?}
 */
build(
      driver: AnimationDriver, element: any, currentState: any, nextState: any,
      options?: AnimationOptions,
      subInstructions?: ElementInstructionMap): AnimationTransitionInstruction|undefined {
    const /** @type {?} */ animationOptions = mergeAnimationOptions(this.ast.options || {}, options || {});

    const /** @type {?} */ backupStateStyles = this._stateStyles['*'] || {};
    const /** @type {?} */ currentStateStyles = this._stateStyles[currentState] || backupStateStyles;
    const /** @type {?} */ nextStateStyles = this._stateStyles[nextState] || backupStateStyles;

    const /** @type {?} */ errors: any[] = [];
    const /** @type {?} */ timelines = buildAnimationTimelines(
        driver, element, this.ast.animation, currentStateStyles, nextStateStyles, animationOptions,
        subInstructions, errors);

    if (errors.length) {
      const /** @type {?} */ errorMessage = `animation building failed:\n${errors.join("\n")}`;
      throw new Error(errorMessage);
    }

    const /** @type {?} */ preStyleMap = new Map<any, {[prop: string]: boolean}>();
    const /** @type {?} */ postStyleMap = new Map<any, {[prop: string]: boolean}>();
    const /** @type {?} */ queriedElements = new Set<any>();
    timelines.forEach(tl => {
      const /** @type {?} */ elm = tl.element;
      const /** @type {?} */ preProps = getOrSetAsInMap(preStyleMap, elm, {});
      tl.preStyleProps.forEach(prop => preProps[prop] = true);

      const /** @type {?} */ postProps = getOrSetAsInMap(postStyleMap, elm, {});
      tl.postStyleProps.forEach(prop => postProps[prop] = true);

      if (elm !== element) {
        queriedElements.add(elm);
      }
    });

    const /** @type {?} */ queriedElementsList = iteratorToArray(queriedElements.values());
    return createTransitionInstruction(
        element, this._triggerName, currentState, nextState, nextState === 'void',
        currentStateStyles, nextStateStyles, timelines, queriedElementsList, preStyleMap,
        postStyleMap);
  }
}

function AnimationTransitionFactory_tsickle_Closure_declarations() {
/** @type {?} */
AnimationTransitionFactory.prototype._triggerName;
/** @type {?} */
AnimationTransitionFactory.prototype.ast;
/** @type {?} */
AnimationTransitionFactory.prototype._stateStyles;
}

/**
 * @param {?} matchFns
 * @param {?} currentState
 * @param {?} nextState
 * @return {?}
 */
function oneOrMoreTransitionsMatch(
    matchFns: TransitionMatcherFn[], currentState: any, nextState: any): boolean {
  return matchFns.some(fn => fn(currentState, nextState));
}
